import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Trophy, Star, X, Zap, Clock } from 'lucide-react';

interface ChallengeResult {
  challengerName: string;
  challengerAvatar?: string;
  challengerScore: number;
  challengerTime?: number; // Time in milliseconds
  accepterName: string;
  accepterAvatar?: string;
  accepterScore: number;
  accepterTime?: number; // Time in milliseconds
  winner: 'challenger' | 'accepter' | 'tie';
  giftAmount: number;
  isCurrentUserWinner: boolean;
  gameType: 'quiz' | 'matching';
}

interface ChallengeResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: ChallengeResult | null;
}

// Format time in milliseconds to mm:ss.ms format
const formatTime = (ms: number | undefined): string => {
  if (ms === undefined || ms === null) return '--:--';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = Math.floor((ms % 1000) / 10);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
};

const confettiAnimation = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ConfettiContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const Confetti = styled.div<{ $delay: number; $left: number; $color: string }>`
  position: absolute;
  top: -20px;
  left: ${props => props.$left}%;
  width: 10px;
  height: 10px;
  background: ${props => props.$color};
  animation: ${confettiAnimation} 3s ease-out forwards;
  animation-delay: ${props => props.$delay}s;
  border-radius: 2px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.5s ease-out;
  position: relative;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: #e5e7eb;
    transform: scale(1.1);
  }
`;

const Header = styled.div<{ $isWinner: boolean }>`
  text-align: center;
  margin-bottom: 24px;
`;

const TrophyIcon = styled.div<{ $isWinner: boolean }>`
  font-size: 64px;
  margin-bottom: 16px;
  animation: ${pulseAnimation} 1s ease-in-out infinite;
`;

const Title = styled.h2<{ $isWinner: boolean }>`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  background: ${props => props.$isWinner 
    ? 'linear-gradient(135deg, #ffd700 0%, #ffb700 100%)' 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: #666;
  font-size: 16px;
`;

const PlayersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
`;

const PlayerCard = styled.div<{ $isWinner: boolean; $isTie: boolean }>`
  flex: 1;
  background: ${props => {
    if (props.$isTie) return 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)';
    return props.$isWinner 
      ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)' 
      : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
  }};
  border: 3px solid ${props => {
    if (props.$isTie) return '#999';
    return props.$isWinner ? '#28a745' : '#dee2e6';
  }};
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  position: relative;
  transition: all 0.3s;

  ${props => props.$isWinner && !props.$isTie && `
    box-shadow: 0 4px 20px rgba(40, 167, 69, 0.3);
    transform: scale(1.02);
  `}
`;

const WinnerBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ffd700 0%, #ffb700 100%);
  color: #333;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
`;

const Avatar = styled.div<{ $src?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$src ? `url(${props.$src})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  background-size: cover;
  background-position: center;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const PlayerName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlayerRole = styled.div`
  font-size: 11px;
  color: #888;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Score = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: #333;
  line-height: 1;
`;

const ScoreLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const TimeDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 8px;
  background: #f0f4ff;
  border-radius: 6px;
  font-size: 13px;
  color: #667eea;
  font-weight: 600;
`;

const VsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
`;

const VsText = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #667eea;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const GiftTransfer = styled.div<{ $isWinner: boolean }>`
  background: ${props => props.$isWinner 
    ? 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)' 
    : 'linear-gradient(135deg, #f8d7da 0%, #f5c2c7 100%)'};
  border-left: 4px solid ${props => props.$isWinner ? '#ffc107' : '#dc3545'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const GiftIcon = styled.div`
  font-size: 32px;
`;

const GiftInfo = styled.div`
  flex: 1;
`;

const GiftTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
`;

const GiftDescription = styled.div`
  font-size: 14px;
  color: #666;
`;

const TieMessage = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left: 4px solid #2196f3;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: center;
`;

const TieText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
`;

const CloseButtonLarge = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
`;

const ChallengeResultModal: React.FC<ChallengeResultModalProps> = ({
  isOpen,
  onClose,
  result,
}) => {
  if (!isOpen || !result) return null;

  const confettiColors = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96e6a1', '#dda0dd'];
  const confettiPieces = result.isCurrentUserWinner 
    ? Array.from({ length: 30 }, (_, i) => ({
        delay: Math.random() * 0.5,
        left: Math.random() * 100,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
      }))
    : [];

  const isTie = result.winner === 'tie';
  const challengerWon = result.winner === 'challenger';
  const accepterWon = result.winner === 'accepter';

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        {result.isCurrentUserWinner && (
          <ConfettiContainer>
            {confettiPieces.map((piece, idx) => (
              <Confetti key={idx} $delay={piece.delay} $left={piece.left} $color={piece.color} />
            ))}
          </ConfettiContainer>
        )}

        <Header $isWinner={result.isCurrentUserWinner}>
          <TrophyIcon $isWinner={result.isCurrentUserWinner}>
            {isTie ? '🤝' : result.isCurrentUserWinner ? '🏆' : '😢'}
          </TrophyIcon>
          <Title $isWinner={result.isCurrentUserWinner}>
            {isTie ? 'Hòa!' : result.isCurrentUserWinner ? 'Chúc mừng! Bạn thắng!' : 'Tiếc quá! Bạn thua!'}
          </Title>
          <Subtitle>
            {result.gameType === 'quiz' ? '🎯 Trắc Nghiệm' : '🃏 Ghép Thẻ'}
          </Subtitle>
        </Header>

        <PlayersContainer>
          <PlayerCard $isWinner={challengerWon} $isTie={isTie}>
            {challengerWon && !isTie && (
              <WinnerBadge>
                <Trophy size={12} /> Thắng
              </WinnerBadge>
            )}
            <Avatar $src={result.challengerAvatar}>
              {!result.challengerAvatar && '👤'}
            </Avatar>
            <PlayerRole>Người thách đấu</PlayerRole>
            <PlayerName>{result.challengerName}</PlayerName>
            <Score>{result.challengerScore}</Score>
            <ScoreLabel>điểm</ScoreLabel>
            <TimeDisplay>
              <Clock size={14} />
              {formatTime(result.challengerTime)}
            </TimeDisplay>
          </PlayerCard>

          <VsContainer>
            <VsText>VS</VsText>
          </VsContainer>

          <PlayerCard $isWinner={accepterWon} $isTie={isTie}>
            {accepterWon && !isTie && (
              <WinnerBadge>
                <Trophy size={12} /> Thắng
              </WinnerBadge>
            )}
            <Avatar $src={result.accepterAvatar}>
              {!result.accepterAvatar && '👤'}
            </Avatar>
            <PlayerRole>Người chấp nhận</PlayerRole>
            <PlayerName>{result.accepterName}</PlayerName>
            <Score>{result.accepterScore}</Score>
            <ScoreLabel>điểm</ScoreLabel>
            <TimeDisplay>
              <Clock size={14} />
              {formatTime(result.accepterTime)}
            </TimeDisplay>
          </PlayerCard>
        </PlayersContainer>

        {isTie ? (
          <TieMessage>
            <TieText>⚖️ Cả hai đều giữ nguyên {result.giftAmount} sao của mình!</TieText>
          </TieMessage>
        ) : (
          <GiftTransfer $isWinner={result.isCurrentUserWinner}>
            <GiftIcon>{result.isCurrentUserWinner ? '⭐' : '💸'}</GiftIcon>
            <GiftInfo>
              <GiftTitle>
                {result.isCurrentUserWinner 
                  ? `+${result.giftAmount} sao!` 
                  : `-${result.giftAmount} sao`}
              </GiftTitle>
              <GiftDescription>
                {result.isCurrentUserWinner
                  ? `Bạn đã giành được ${result.giftAmount} sao từ đối thủ!`
                  : `${result.giftAmount} sao của bạn đã được chuyển cho người thắng.`}
              </GiftDescription>
            </GiftInfo>
          </GiftTransfer>
        )}

        <CloseButtonLarge onClick={onClose}>
          Đóng
        </CloseButtonLarge>
      </Modal>
    </Overlay>
  );
};

export default ChallengeResultModal;
